import {
  useCreate,
  useNotify,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";
import { useEffect, useId, useState, type FormEvent } from "react";

import { toIsoDay } from "@/components/market-intel/dates";
import {
  DateField,
  Field,
  FieldError,
  FormDialog,
  SELECT_CLASS,
} from "@/components/market-intel/FormDialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";

import {
  ACTIVITY_KINDS,
  activityFormDefaults,
  activityKindLabelKey,
  normalizeActivityForm,
  type ActivityFormValues,
} from "./intel";
import { PLATFORMS, platformLabelKey } from "./platforms";
import { FormActions } from "./ui";
import type {
  ActivityKind,
  CompetitorMarketingActivity,
  SocialPlatform,
} from "./types";

export const ActivityForm = ({
  open,
  onOpenChange,
  competitorId,
  item,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  competitorId: Identifier;
  /** Edits this item; adds one when null. */
  item: CompetitorMarketingActivity | null;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const formId = useId();
  const today = toIsoDay(new Date());
  const [values, setValues] = useState(() => activityFormDefaults(today));
  const [error, setError] = useState<string | null>(null);
  const [create, { isPending: creating }] = useCreate();
  const [update, { isPending: updating }] = useUpdate();

  useEffect(() => {
    if (open) {
      setValues(activityFormDefaults(today, item ?? undefined));
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item]);

  const set =
    <K extends keyof ActivityFormValues>(key: K) =>
    (value: ActivityFormValues[K]) => {
      setValues((current) => ({ ...current, [key]: value }));
      setError(null);
    };
  const close = () => onOpenChange(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const result = normalizeActivityForm(competitorId, values);
    if (!result.ok) return setError(result.error);
    const options = {
      onSuccess: () => {
        notify("competition.form.saved", { type: "success" });
        close();
      },
      onError: () => notify("competition.form.error", { type: "error" }),
    };
    if (item)
      update(
        "competitor_marketing_activity",
        { id: item.id, data: result.data, previousData: item },
        options,
      );
    else
      create("competitor_marketing_activity", { data: result.data }, options);
  };

  const errorId = `${formId}-error`;
  const titleError =
    error === "competition.form.title_required" ||
    error === "competition.form.title_too_long";
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={translate(
        item ? "competition.activity.edit" : "competition.activity.add",
      )}
      formId={formId}
      onSubmit={submit}
      actions={
        <FormActions
          formId={formId}
          disabled={creating || updating}
          onCancel={close}
        />
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field
          id={`${formId}-platform`}
          label={translate("competition.platforms.label")}
        >
          <select
            id={`${formId}-platform`}
            className={SELECT_CLASS}
            value={values.platform}
            onChange={(event) =>
              set("platform")(event.target.value as SocialPlatform)
            }
          >
            {PLATFORMS.map((platform) => (
              <option key={platform} value={platform}>
                {translate(platformLabelKey(platform))}
              </option>
            ))}
          </select>
        </Field>
        <Field
          id={`${formId}-kind`}
          label={translate("competition.activity.kinds.label")}
        >
          <select
            id={`${formId}-kind`}
            className={SELECT_CLASS}
            value={values.kind}
            onChange={(event) =>
              set("kind")(event.target.value as ActivityKind)
            }
          >
            {ACTIVITY_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {translate(activityKindLabelKey(kind))}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field
        id={`${formId}-title`}
        label={translate("competition.activity.form.title")}
      >
        <Input
          id={`${formId}-title`}
          value={values.title}
          onChange={(event) => set("title")(event.target.value)}
          aria-invalid={titleError || undefined}
          aria-describedby={titleError ? errorId : undefined}
          autoFocus={!isMobile}
          maxLength={220}
        />
      </Field>
      {error ? <FieldError id={errorId}>{translate(error)}</FieldError> : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          id={`${formId}-published_at`}
          label={translate("competition.activity.form.published_at")}
        >
          <DateField
            id={`${formId}-published_at`}
            value={values.published_at}
            onChange={(event) => set("published_at")(event.target.value)}
          />
        </Field>
        <Field
          id={`${formId}-url`}
          label={translate("competition.activity.form.url")}
        >
          <Input
            id={`${formId}-url`}
            type="url"
            inputMode="url"
            value={values.url}
            onChange={(event) => set("url")(event.target.value)}
          />
        </Field>
      </div>
      <Field
        id={`${formId}-description`}
        label={translate("competition.activity.form.description")}
      >
        <Textarea
          id={`${formId}-description`}
          value={values.description}
          onChange={(event) => set("description")(event.target.value)}
          rows={3}
        />
      </Field>
      <Field
        id={`${formId}-notes`}
        label={translate("competition.activity.form.notes")}
      >
        <Textarea
          id={`${formId}-notes`}
          value={values.notes}
          onChange={(event) => set("notes")(event.target.value)}
          rows={2}
        />
      </Field>
      <p className="text-xs text-muted-foreground">
        {translate("competition.form.markdown_hint")}
      </p>
    </FormDialog>
  );
};
